/**
 * Moments on a video or a live event (markers): ids are made, never typed; moves are numbered for
 * the apps' "Round 3 of 7" from their order; a recipe step's clip is a step moment the step points
 * at. Pure, unit-tested.
 */

export interface Marker {
  id: string;
  kind: string;
  startSeconds: number;
  endSeconds?: number;
  title?: Record<string, string>;
  data?: Record<string, string | number | boolean>;
}

/** A fresh id for a moment of this kind: move-1, move-2… skipping what is taken. */
export function nextId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  let n = 1;
  while (used.has(`${prefix}-${n}`)) n++;
  return `${prefix}-${n}`;
}

export const byStart = (a: Marker, b: Marker) => a.startSeconds - b.startSeconds;

/** Moves get their round and the number of rounds from their order in time; other moments are left alone. */
export function withRounds(markers: Marker[]): Marker[] {
  const moves = markers.filter((m) => m.kind === "move").sort(byStart);
  const round = new Map(moves.map((m, i) => [m.id, i + 1]));
  return markers.map((m) => (m.kind === "move" ? { ...m, data: { ...(m.data ?? {}), round: round.get(m.id)!, rounds: moves.length } } : m));
}

/** A moment as it is saved: no empty title, no undefined end, no empty data. */
export function cleanMarker(m: Marker): Marker {
  const out: Marker = { id: m.id, kind: m.kind, startSeconds: Math.max(0, m.startSeconds) };
  if (m.endSeconds !== undefined && Number.isFinite(m.endSeconds)) out.endSeconds = m.endSeconds;
  const title = Object.fromEntries(Object.entries(m.title ?? {}).filter(([, v]) => v.trim()));
  if (Object.keys(title).length) out.title = title;
  const data = Object.fromEntries(Object.entries(m.data ?? {}).filter(([, v]) => v !== undefined && v !== ""));
  if (Object.keys(data).length) out.data = data;
  return out;
}

/**
 * A recipe step's clip: sets (or clears, with null) the step moment the step points at. Returns
 * the new markers and the step's markerId.
 */
export function setStepClip(markers: Marker[], step: { id: string; markerId?: string }, clip: { start: number; end?: number } | null): { markers: Marker[]; markerId?: string } {
  const id = step.markerId ?? `step-${step.id}`;
  const others = markers.filter((m) => m.id !== id);
  if (!clip) return { markers: others };
  const existing = markers.find((m) => m.id === id);
  const marker = cleanMarker({ ...(existing ?? { id, kind: "step" }), id, kind: "step", startSeconds: clip.start, endSeconds: clip.end });
  return { markers: [...others, marker].sort(byStart), markerId: id };
}
